import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { LIMIT } from "../../../../constants";
import { Product, SellerProductDetail } from "../../../../interfaces";
import { createFileRoute, useLocation } from "@tanstack/react-router";
import { InfiniteScrollList } from "@/components/common/scroll";
import './index.css';
import { currencyFormatter } from "@/utils";
import { Button } from "@/components/ui/button";
import { Search } from "@/components/ui/Search";
import { debounce } from "lodash";
import {handlePrintBarcode} from "@/features/product";
import { currentUserSelector } from "@/features/auth";
import { useGetSellerProductListQuery } from "@/features/seller";
import { useNavigate } from "@tanstack/react-router";
import { useBarcodeConfig } from "@/features/product/hooks/useBarcodeConfig";
export const Route = createFileRoute("/(app)/(store)/product/")({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [limit , setLimit] = useState(LIMIT *2)
  const [productSearch, setProductSearch] = useState<string>("");
  const storeInfo = useSelector(currentUserSelector);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [page, setPage] = useState<number>(1);
  const { data: productList = [], isFetching } = useGetSellerProductListQuery({
    search: productSearch,
    limit,
    page,
  });
  useEffect(() => {
    setPage(1);
    setLimit(LIMIT*2)
  }, [productSearch]);

  const loadMore = () => {
    if (hasMore) {
      setPage((prevPage) => prevPage + 1);
    }
  };
  const hasMore = !isFetching && productList.length === page * limit;
  const handleSelectProduct = (product: Product) => {
    setSelectedProduct(product);
    navigate({ hash: product._id });
  };

  const handleSearch = useCallback(
    debounce((value: string) => {
      setProductSearch(value);
      setPage(1);
    }, 500),
    []
  );
  const renderItem = (product: Product) => {
    const item = product.sellerProductDetail?.at(0);
    return (
      <div
        className="productItemProduct"
        key={product._id}
        onClick={() => handleSelectProduct(product)}
      >
        <div className="productItemGradientProduct">
          <div className="productItemHeaderProduct">
            <div className="productCardProduct">
              {<img src={product.imageList?.at(0)} width={80} height={80} />}
              <div>
                <p className="billTotalProduct">
                  {product.name}
                </p>
                <p>
                  {item?.barcode && <span>Barcode : {item?.barcode}</span>}
                  {item?.productCode && <span>Product Code{item?.productCode}</span>}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="productItemContainerProduct">
          <div className="productItemContentProduct">
            {!!item?.variationInfo?.size?.name && (
              <div className="chipProduct">
                <p>Size : {item?.variationInfo?.size?.name}</p>
              </div>
            )}
            {!!item?.variationInfo?.color && (
              <div className="chipProduct">
                <p>Color : {item?.variationInfo?.color?.name}</p>
              </div>
            )}
            {!!item?.variationInfo?.weight && (
              <div className="chipProduct">
                <p>
                  Weight : {item?.variationInfo?.weight?.quantity}/
                  {item?.variationInfo?.weight?.unit}
                </p>
              </div>
            )}
          </div>
          <div className="productItemContentProduct">
            {!!item?.price?.mrp && (
              <div className="chipProduct">
                <p>MRP : {currencyFormatter(item.price?.mrp)}</p>
              </div>
            )}
            {!!item?.price?.sellingPrice && (
              <div className="chipProduct">
                <p>Selling Price : {currencyFormatter(item.price?.sellingPrice)}</p>
              </div>
            )}
            {!!item?.price?.purchasePrice && (
              <div className="chipProduct">
                <p>Purchase Price: {currencyFormatter(item.price?.purchasePrice)}</p>
              </div>
            )}
            {!!item?.stock?.quantity && (
              <div className="chipProduct">
                <p>
                  Quantity: {item?.stock?.quantity}/{item?.stock?.productUnit}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  return (
    <div className="layoutProduct">
      <main className="mainProduct">
        <div className="searchContainerProduct">
          <Search
            placeholder="Search products..."
            onChange={(e) => handleSearch(e.target.value)}
            className="searchInputProduct"
          />
        </div>
        <div className="listContainerProduct">
          <InfiniteScrollList
            items={productList as Array<any>}
            loadMore={loadMore}
            hasMore={hasMore }
            isLoading={isFetching}
            renderItem={renderItem}
            maxHeight={"100dvh"}
          />
        </div>
      </main>
      <aside className="sideProduct">
        {selectedProduct && <ProductDetailSection product={selectedProduct} />}
      </aside>
    </div>
  );
}

function ProductDetailSection({ product }: { product: Product }) {
  const storeInfo = useSelector(currentUserSelector);
  const { barcodeConfig } = useBarcodeConfig();
  useEffect(()=>{} , [barcodeConfig])

  const handlePrint = async (item: SellerProductDetail) => {
    try {
      await handlePrintBarcode({
        mrp: item.price.mrp,
        sellingPrice: item.price.sellingPrice,
        productName: product.name,
        barcode: item.barcode,
        slug: item.variationId,
        storeName: storeInfo?.name as string,
        productCode: item?.productCode as string,
        quantity: item.stock?.quantity,
      }); 
    } catch (error) {
      console.error('Print error:', error);
    }
  };


  const renderItem = (item: SellerProductDetail) => (
    <div className="productItemProduct marginBottomProduct" key={item._id}>
      <div className="productItemGradientProduct">
        <div className="productItemHeaderProduct">
          <div className="productCardProduct">
            {
              <img
                src={item.variationInfo?.imageList?.at(0)}
                width={80}
                height={80}
              />
            }
            <p className="billTotalProduct">
              {product.name}
            </p>
          </div>
        </div>
      </div>

      <div className="productItemContentProduct">
        {item.variationInfo?.size?.name && (
          <div className="chipProduct">
            <p>Size : {item?.variationInfo?.size?.name}</p>
          </div>
        )}
        {item?.variationInfo?.color && (
          <div className="chipProduct">
            <p>Color : {item?.variationInfo?.color?.name}</p>
          </div>
        )}
        {item.variationInfo?.weight && (
          <div className="chipProduct">
            <p>
              Weight : {item?.variationInfo?.weight?.quantity}/
              {item?.variationInfo?.weight?.unit}
            </p>
          </div>
        )}
      </div>

      <div className="productItemContentProduct">
        {item?.price?.mrp && (
          <div className="chipProduct">
            <p>MRP : {currencyFormatter(item.price?.mrp)}</p>
          </div>
        )}
        {item?.price?.sellingPrice && (
          <div className="chipProduct">
            <p>Selling Price : {currencyFormatter(item.price?.sellingPrice)}</p>
          </div>
        )}
        {item?.price?.purchasePrice && (
          <div className="chipProduct">
            <p>Purchase Price: {currencyFormatter(item.price?.purchasePrice)}</p>
          </div>
        )}
      </div>

      <div className="productItemContentProduct">
        {item?.stock?.quantity && (
          <div className="chipProduct">
            <p>
              Quantity: {item?.stock?.quantity}/{item?.stock?.productUnit}
            </p>
          </div>
        )}
        {item?.minimumQuantity && (
          <div className="chipProduct">
            <p>Min Quantity Sell: {item?.minimumQuantity?.quantity}</p>
          </div>
        )}
        {item?.stock && (
          <div className="chipProduct">
            <p>Min Alert Quantity : {item?.stock?.minimumAlertQuantity}</p>
          </div>
        )}
      </div>

      <div className="productItemContentProduct buttonsProduct">
        <Button variant="link" to={`/product/${product._id}/update`}>
          Edit Product Information{" "}
        </Button>
        {!!item.barcode && (
          <Button
            type="button"
            onClick={() => handlePrint(item)} 
          >
            Print Barcode{" "}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div>
      <InfiniteScrollList
        items={product.sellerProductDetail}
        renderItem={renderItem}
        maxHeight={"100dvh"}
        hasMore={false}
        isLoading={false}
      />
    </div>
  );
}